import { ReactNode, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Col,
  Container,
  Dropdown,
  DropdownButton,
  Row,
  Table,
  Popover,
  OverlayTrigger,
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import KAccess from '../components/KAccess'
import KButton from '../components/KButton'
import KConfirm from '../components/KConfirm'
import KLayerNavigation from '../components/KLayerNavigation'
import ServiceCostSubtitle from '../components/ServiceCostSubtitle'
import {
  canEditKycForms,
  getAccessToken,
  getCompanyInfo,
  getUserAccess,
  isUserLoggedIn,
  setAccessToken,
  setUserAccess,
  showKAlert,
} from '../helpers'
import { ensureEnoughCredit } from '../helpers/credit.helper'
import { t } from '../locale'
import { Client } from '../react-app-env'
import {
  clientDelete,
  fetchAccess,
  fetchUserEmail,
  getStammdatenClients,
  performAmlCheckOneByClientId,
  performKycCheckOne,
  setClientActiveStatus,
  softDeleteClient,
  validatePinCode,
} from '../service/client'
import { StammdatenShareModal } from '../stammdaten/StammdatenShareModal'
import AmlChecks from '../components/AmlChecks'
import { fetchServiceCosts, ServiceCosts } from '../service/subscription'
import { getCountryRiskColor } from '../helpers/stammdaten.helper'
import { getCountryData } from '../service/country'

export default function Stammdaten() {
  const navigate = useNavigate()
  const [clients, setClients] = useState([] as Array<Client>)
  const [loading, setLoading] = useState(false)
  const [countryRisks, setCountryRisks] = useState<Record<string, number | null>>({})
  const [countryNames, setCountryNames] = useState<Record<string, string>>({})

  const [pin, setPin] = useState('')
  const [access, setAccess] = useState(false)
  const [pinRequired, setPinRequired] = useState(true)
  const pendingAction = useRef<'kyc' | 'aml' | 'share'>('kyc')
  const [globalSettings, setGlobalSettings] = useState({
    kycCheckEnabled: true,
    amlSearchEnabled: true,
  })
  const [serviceCosts, setServiceCosts] = useState<ServiceCosts | null>(null)
  const [accessSubtitle, setAccessSubtitle] = useState<ReactNode>(undefined)
  const [state, setState] = useState({
    showModal: false,
    action: '',
    confirmShow: false,
    dataForExport: [] as any[],
    allClients: [] as Array<Client>,
    pin: '',
    kycChecks: [] as any[],
    kycCheckButton: 'btn-user-secondary',
  })

  const noFilter: any = {
    stammdatenExists: true,
    keyword: '',
    isPerson: undefined,
    sortBy: 'client',
    risk: 'all',
    active: true,
    deletedAt: '',
  }
  const [filter, setFilter] = useState(noFilter)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [clientStatus, setClientStatus] = useState<'active' | 'inactive' | 'deleted'>('active')

  const [confirmAction, setConfirmAction] = useState<(() => Promise<void>) | null>(null)
  const [confirmMessage, setConfirmMessage] = useState('')

  const requestConfirm = (message: string, action: () => Promise<void>) => {
    setConfirmMessage(message)
    setConfirmAction(() => action)
  }

  const handleConfirm = async () => {
    if (confirmAction) await confirmAction()
    setConfirmAction(null)
    setConfirmMessage('')
  }

  const handleCancelConfirm = () => {
    setConfirmAction(null)
    setConfirmMessage('')
  }

  const changeClientStatus = (status: 'active' | 'inactive' | 'deleted') => {
    setClientStatus(status)
    setFilter((prev: any) => ({
      ...prev,
      active: status === 'deleted' ? undefined : status === 'active',
      deletedAt: status === 'deleted' ? 'notEmpty' : '',
    }))
  }

  const toggleClientActive = (client: Client) => {
    const isActivating = clientStatus !== 'active'
    const message = isActivating ? t('activateClient') : t('deactivateClient')
    requestConfirm(message, async () => {
      // From the inactive or deleted view this restores the client as active
      // (setClientActiveStatus also clears deletedAt).
      const response = await setClientActiveStatus(client, isActivating)
      if (response.ok) {
        setClients(prev => prev.filter(c => c._id !== client._id))
      }
    })
  }

  const deleteClientRecord = (client: Client) => {
    const isDeletedView = clientStatus === 'deleted'
    const message = isDeletedView ? t('deleteClient') : t('markDeleted')
    requestConfirm(message, async () => {
      const response = isDeletedView
        ? await clientDelete(client._id)
        : await softDeleteClient(client._id, client.clientNumber)
      if (response.ok) {
        setClients(prev => prev.filter(c => c._id !== client._id))
      }
    })
  }

  const updateState = (newState: Partial<typeof state>) =>
    setState(prevState => ({ ...prevState, ...newState }))

  const [showModal, setShowModal] = useState(false)
  const [kycChecksNew, setKycChecksNew] = useState(false)

  const canEdit = canEditKycForms()
  const isAdmin = !!getUserAccess()?.permissions?.admin

  useEffect(() => {
    userAccess()
    fetchClients()
  }, [filter])

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/global-settings`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data) setGlobalSettings(data)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchServiceCosts().then(setServiceCosts)
  }, [])

  const userAccess = async () => {
    const userResponse = await fetchUserEmail()
    if (userResponse?.user?.email) {
      localStorage.setItem('email', userResponse.user.email)
    }
  }
  const createStammdaten = async (type: 'company' | 'person') => {
    // Stammdaten creation costs credit - check before opening the form.
    if (!(await ensureEnoughCredit('stammdaten'))) return

    navigate(`/client/stammdaten/${type}/new`)
  }

  const fetchClients = async () => {
    const response = await getStammdatenClients(filter)
    const clientsData = response.clients || []

    const uniqueCountries = [...new Set(clientsData.map((c: Client) => c.country).filter(Boolean))]
    const promises = uniqueCountries.map(async country => {
      try {
        const countryData = await getCountryData(country as string)
        return { country, risk: countryData?.risk ?? null, name: countryData?.name ?? null }
      } catch {
        return { country, risk: null, name: null }
      }
    })
    const results = await Promise.all(promises)
    const newRisks: Record<string, number | null> = {}
    const newNames: Record<string, string> = {}
    results.forEach(({ country, risk, name }) => {
      newRisks[country as string] = risk
      if (name) newNames[country as string] = name
    })
    setCountryRisks(newRisks)
    setCountryNames(newNames)
    setClients(clientsData)
  }

  const getAccess = async () => {
    const result = await fetchAccess(localStorage.getItem('email'))

    if (!!result.accessToken) {
      setAccessToken(result.accessToken)
      setPin(result.pinCode)
      setPinRequired(result.pinRequired !== false)
      setAccess(true)
    }
  }

  const checkPin = async (pinCode: number) => {
    try {
      const result = await validatePinCode(pinCode)

      if (result?.userAccess) {
        setUserAccess(result.userAccess)
        setAccess(false)
      }

      if (pendingAction.current === 'aml') {
        await amlSearchOne()
      } else if (pendingAction.current === 'kyc') {
        const response = await kycCheck()
        if (response) {
          setKycChecksNew(true)
        }
      } else {
        setShowModal(true)
      }
    } catch (error) {
      console.error('Error validating pin code:', error)
    }
  }

  const kycCheck = async () => {
    try {
      setLoading(true)

      const result = await performKycCheckOne({
        clientId: localStorage.getItem('clientId'),
        clientNumber: localStorage.getItem('clientNumber'),
        firstName: localStorage.getItem('firstName'),
        lastName: localStorage.getItem('name'),
        dateOfBirth: localStorage.getItem('dateOfBirth') || '',
        country: localStorage.getItem('country') || '',
        accessToken: getAccessToken() ?? '',
        companyInfo: getCompanyInfo(),
      })

      setKycChecksNew(true)
      updateState({ kycChecks: result[0] })
      return result
    } catch (error) {
      return []
    } finally {
      setLoading(false)
    }
  }

  const amlSearchOne = async () => {
    try {
      setLoading(true)

      const clientId = localStorage.getItem('clientId')
      if (!clientId) return

      // Send only the clientId so the backend expands the check to the client's
      // representatives and beneficial owners (legalEntities,
      // foundationsLegalCapacity, instigationPersons) and charges per person —
      // same as the check button in the client list.
      const result = await performAmlCheckOneByClientId(clientId, getAccessToken() ?? '')

      // The screening runs in the background — the user gets an email and can
      // see the result on the client page once it lands.
      if (result?.ok) {
        showKAlert('success', t('emailAfterCheck'))
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false)
    }
  }
  const closeModal = () => {
    setShowModal(false)
  }

  const shareStammdaten = async () => {}

  const generatePDFForClient = (client: Client) => {
    window.open(
      `/client/stammdaten/${client.isPerson === 1 ? 'person' : 'company'}/${client._id}?pdf=true`,
      '_blank'
    )
  }

  const handleIsPerson = (e: any) => {
    const value = e.target.value
    setFilter((prevFilter: any) => ({
      ...prevFilter,
      isPerson: value,
    }))
  }

  const handleRiskFilter = (value: boolean | null) => {
    setFilter((prev: any) => ({
      ...prev,
      risk: value,
    }))
  }

  const showAll = () => {
    setSearchKeyword('')
    setClientStatus('active')
    setFilter(noFilter)
  }

  return (
    <Container>
      <Row>
        <div className='sidebar col-md-3 col-lg-2 p-0'>
          <KLayerNavigation logged={isUserLoggedIn()} />
        </div>
        <main className='col-md-9 col-lg-10 ps-2 ps-md-3 px-md-4'>
          <Row>
            <Col className='col-sm-9'>
              <Card.Title>
                <h4>{t('kycStammdaten')}</h4>
              </Card.Title>
            </Col>
            <Col className=''>
              <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3'>
                <input
                  type='search'
                  className='border border-primary rounded-pill px-2'
                  placeholder={t('search') + '...'}
                  aria-label='Search'
                  value={searchKeyword}
                  autoComplete='on'
                  onChange={e => {
                    setSearchKeyword(e.target.value)
                    setFilter((prev: any) => ({ ...prev, keyword: e.target.value }))
                  }}
                />
                <div className='btn-toolbar mb-2 mb-md-0 ms-auto'>
                  <Button
                    variant='outline-primary'
                    title={t('naturalPerson')}
                    onClick={() => showAll()}
                  >
                    {t('all')}
                  </Button>
                  <KButton
                    variant='outline-primary'
                    type='button'
                    icon='person-fill'
                    className='ms-5'
                    title={t('naturalPerson')}
                    onClick={() => handleIsPerson({ target: { value: 'yes' } })}
                  />
                  <KButton
                    variant='outline-primary'
                    type='button'
                    icon='building-fill'
                    className='btn-spacing'
                    title={t('company')}
                    onClick={() => handleIsPerson({ target: { value: 'no' } })}
                  />

                  <KButton
                    variant='outline-secondary'
                    type='button'
                    icon='star-fill'
                    className='btn-spacing ms-5'
                    title={t('notTested')}
                    onClick={() => handleRiskFilter(null)}
                  />
                  <KButton
                    variant='outline-success'
                    type='button'
                    icon='star-fill'
                    className='btn-spacing'
                    title={t('success')}
                    onClick={() => handleRiskFilter(false)}
                  />
                  <KButton
                    variant='outline-danger'
                    type='button'
                    icon='star-fill'
                    className='btn-spacing'
                    title={t('danger')}
                    onClick={() => handleRiskFilter(true)}
                  />
                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement='top'
                    overlay={
                      <Popover id='stammdaten-company-popover'>
                        <Popover.Body className='small'>{t('stammdatenCostInfo')}</Popover.Body>
                      </Popover>
                    }
                  >
                    <Button
                      variant='white'
                      type='button'
                      className='stammdaten-btn btn-sm border-blue text-blue ms-5'
                      onClick={() => createStammdaten('company')}
                      disabled={loading}
                    >
                      {t('company')}
                    </Button>
                  </OverlayTrigger>

                  <OverlayTrigger
                    trigger={['hover', 'focus']}
                    placement='top'
                    overlay={
                      <Popover id='stammdaten-person-popover'>
                        <Popover.Body className='small'>{t('stammdatenCostInfo')}</Popover.Body>
                      </Popover>
                    }
                  >
                    <Button
                      variant='white'
                      type='button'
                      className='stammdaten-btn btn-sm border-blue text-blue ms-2'
                      onClick={() => createStammdaten('person')}
                      disabled={loading}
                    >
                      {t('naturlichePerson')}
                    </Button>
                  </OverlayTrigger>
                </div>
              </div>
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col className='d-flex justify-content-end align-items-center gap-2'>
              <label htmlFor='stammdaten-status-filter' className='small text-muted mb-0'>
                {t('status')}
              </label>
              <select
                id='stammdaten-status-filter'
                className='form-select form-select-sm border-primary rounded-pill w-auto'
                value={clientStatus}
                onChange={e =>
                  changeClientStatus(e.target.value as 'active' | 'inactive' | 'deleted')
                }
              >
                <option value='active'>{t('active')}</option>
                <option value='inactive'>{t('inactive')}</option>
                <option value='deleted'>{t('deleted')}</option>
              </select>
            </Col>
          </Row>
          <Row className='mt-3 mb-3'>
            <Col>
              <div className='table-responsive pb-3'>
                <Table id='kyc-table' className='border-bottom border-top fs-6'>
                  <thead>
                    <tr className='table-light small'>
                      <th className='p-0 align-middle'>{t('Nr.')}</th>
                      <th className='align-middle'>{t('naturlichePerson')}</th>
                      <th className='align-middle'>{t('name')}</th>
                      <th className='align-middle'>{t('firstName')}</th>
                      <th className='align-middle'>{t('country')}</th>
                      <th className='align-middle'>{t('lastCheck')}</th>
                      <th className='align-middle'>{t('actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(clients) && clients.length > 0 ? (
                      clients.map((c, idx) => (
                        <tr key={c._id ?? idx}>
                          <td className='p-0 align-middle'>{c.clientNumber ?? ''}</td>

                          <td className='align-middle'>{c.isPerson === 1 ? t('yes') : t('no')}</td>

                          <td className='align-middle'>{c.name || '—'}</td>
                          <td className='align-middle'>{c.firstName || '—'}</td>
                          <td className='align-middle'>
                            <span className={`${getCountryRiskColor(c.country, countryRisks)}`}>
                              {countryNames[c.country] || c.country}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`${c.risk === true ? 'border border-danger px-1 rounded text-danger' : 'border border-success px-1 rounded text-success'}`}
                            >
                              {c.lastCheck || ''}
                            </span>
                          </td>
                          <td className='align-middle'>
                            <div className='d-flex gap-2'>
                              {globalSettings.kycCheckEnabled &&
                                canEdit &&
                                clientStatus === 'active' && (
                                  <KButton
                                    variant={c.risk ? 'success' : 'danger'}
                                    disabled={c.name == null}
                                    size='sm'
                                    icon='check'
                                    title={t('check')}
                                    onClick={() => {
                                      pendingAction.current = 'kyc'
                                      setAccessSubtitle(undefined)
                                      localStorage.setItem('clientId', c._id.toString())
                                      localStorage.setItem('name', c.name)
                                      localStorage.setItem('firstName', c.firstName)
                                      localStorage.setItem('country', c.country ?? '')
                                      localStorage.setItem('dateOfBirth', c.dateOfBirth ?? '')
                                      localStorage.setItem(
                                        'clientNumber',
                                        c.clientNumber?.toString() ?? ''
                                      )
                                      getAccess()
                                    }}
                                  />
                                )}
                              {globalSettings.amlSearchEnabled &&
                                canEdit &&
                                clientStatus === 'active' && (
                                  <KButton
                                    variant={c.risk ? 'success' : 'danger'}
                                    disabled={!c.name}
                                    size='sm'
                                    icon='check'
                                    title='AML Search'
                                    className={`${!c.name ? 'disabled' : ''}`}
                                    onClick={() => {
                                      pendingAction.current = 'aml'
                                      setAccessSubtitle(
                                        <ServiceCostSubtitle
                                          label='AML Search'
                                          points={serviceCosts?.amlSearch}
                                        />
                                      )
                                      localStorage.setItem('clientId', c._id.toString())
                                      localStorage.setItem('name', c.name)
                                      localStorage.setItem('firstName', c.firstName)
                                      localStorage.setItem('country', c.country ?? '')
                                      localStorage.setItem('dateOfBirth', c.dateOfBirth ?? '')
                                      localStorage.setItem(
                                        'clientNumber',
                                        c.clientNumber?.toString() ?? ''
                                      )
                                      getAccess()
                                    }}
                                  />
                                )}
                              <KButton
                                size='sm'
                                variant='collapse'
                                className='btn-user-view'
                                icon='eye'
                                title={t('view')}
                                onClick={() => navigate(`/client/stammdaten/${c._id}/view`)}
                              />
                              {clientStatus === 'active' && (
                                <KButton
                                  variant='warning'
                                  icon='pencil-square'
                                  className='btn-user-edit text-white btn-spacing'
                                  title={t('editStammdaten')}
                                  onClick={() =>
                                    navigate(
                                      `/client/stammdaten/${c.isPerson === 1 ? 'person' : 'company'}/${c._id}`
                                    )
                                  }
                                />
                              )}
                              <KButton
                                variant='gray-light'
                                icon='folder2-open'
                                disabled={!c.name}
                                className={`${!c.name ? 'disabled' : ''} btn-user-edit text-white btn-spacing`}
                                title={t('documents')}
                                onClick={() => {
                                  if (!c.clientNumber) {
                                    alert(t('addClientNumberFirst'))
                                    return
                                  }
                                  localStorage.setItem('clientId', c._id)
                                  navigate(`/client/documents/${c.clientNumber}`)
                                }}
                              />
                              {canEdit && clientStatus === 'active' && (
                                <>
                                  <KButton
                                    variant={
                                      (c.stammdaten.shared?.length ?? 0) === 0
                                        ? 'gray-light'
                                        : 'warning'
                                    }
                                    icon='arrow-90deg-right'
                                    className='btn-user-edit text-white btn-spacing'
                                    title={t('share')}
                                    onClick={() => {
                                      pendingAction.current = 'share'
                                      setAccessSubtitle(undefined)
                                      localStorage.setItem('clientId', c._id)
                                      getAccess()
                                    }}
                                  />
                                  <KButton
                                    variant='blue'
                                    label='PDF'
                                    className='btn-user-edit text-white btn-spacing'
                                    title={t('exportPDF')}
                                    onClick={() => generatePDFForClient(c)}
                                  />
                                </>
                              )}
                              {isAdmin && clientStatus !== 'active' && (
                                <KButton
                                  variant='success'
                                  icon='arrow-counterclockwise'
                                  className='text-white btn-spacing'
                                  title={t('activateClient')}
                                  onClick={() => toggleClientActive(c)}
                                />
                              )}
                              {isAdmin && (
                                <>
                                  <div
                                    style={{
                                      width: '1px',
                                      height: '20px',
                                      backgroundColor: '#dee2e6',
                                    }}
                                  />
                                  <DropdownButton
                                    id='dropdown-stammdaten-actions'
                                    variant='outline-secondary'
                                    size='sm'
                                    title={<i className='bi bi-three-dots-vertical'></i>}
                                    drop={idx === clients.length - 1 ? 'up' : 'down'}
                                  >
                                    {clientStatus === 'active' && (
                                      <Dropdown.Item onClick={() => toggleClientActive(c)}>
                                        <i className='bi bi-person-dash'></i>{' '}
                                        {t('deactivateClient')}
                                      </Dropdown.Item>
                                    )}
                                    <Dropdown.Item
                                      onClick={() => deleteClientRecord(c)}
                                      className='text-danger'
                                    >
                                      <i className='bi bi-trash'></i>{' '}
                                      {clientStatus === 'deleted'
                                        ? t('deleteClient')
                                        : t('markDeleted')}
                                    </Dropdown.Item>
                                  </DropdownButton>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className='text-center text-muted py-4'>
                          {t('No data')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
          {access && (
            <KAccess
              show={access}
              title={pinRequired ? t('pinCodeSent') : t('confirmAction')}
              pin={pin}
              pinRequired={pinRequired}
              subtitle={accessSubtitle}
              onConfirm={(pin: any) => {
                checkPin(pin)
              }}
              onClose={() => setAccess(false)}
            />
          )}

          {showModal && (
            <StammdatenShareModal
              show={showModal}
              onClose={closeModal}
              onAccept={shareStammdaten}
            ></StammdatenShareModal>
          )}

          <KConfirm
            show={!!confirmAction}
            message={confirmMessage}
            onConfirm={handleConfirm}
            onCancel={handleCancelConfirm}
          />

          {kycChecksNew && (
            <AmlChecks
              show={kycChecksNew}
              aml={state.kycChecks}
              onClose={() => setKycChecksNew(false)}
              // onDelete={(kycNumber: number) =>
              //     kycCheckDelete(kycNumber)
              // }
            />
          )}
        </main>
      </Row>
    </Container>
  )
}
